import { getSession, setSession, updateAccessToken } from '@/features/auth/auth-store';

/**
 * In dev `vite.config.ts` proxies `/v1/*` to the API, so we fetch relative
 * paths. In production the web and API live on separate origins, so Vite
 * injects the API base URL at build time via VITE_API_BASE_URL.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path}`;
}

export interface ApiErrorPayload {
  error: { code: string; message: string; details?: unknown };
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.error.message);
    this.status = status;
    this.code = payload.error.code;
    this.details = payload.error.details;
  }
}

interface RefreshResponse {
  accessToken: string;
  accessTokenExpiresAt: number;
}

let refreshPromise: Promise<RefreshResponse | null> | null = null;

async function refreshAccessToken(): Promise<RefreshResponse | null> {
  if (!refreshPromise) {
    refreshPromise = fetch(apiUrl('/v1/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json()) as RefreshResponse;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

async function doFetch(path: string, options: ApiRequestOptions): Promise<Response> {
  const headers = new Headers(options.headers);
  if (!headers.has('content-type') && options.body !== undefined) {
    headers.set('content-type', 'application/json');
  }
  const session = getSession();
  if (!options.skipAuth && session) {
    headers.set('authorization', `Bearer ${session.accessToken}`);
  }
  return fetch(apiUrl(path), {
    ...options,
    headers,
    credentials: 'include',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

/**
 * Authenticated fetch that runs the same access-token + refresh dance as
 * apiRequest, but returns the raw Response so callers can stream binary
 * payloads (file downloads, etc.). On non-2xx it throws ApiError, parsing a
 * JSON error body when present.
 */
export async function apiFetch(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  let res = await doFetch(path, options);
  if (res.status === 401 && !options.skipAuth && !options.skipRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      updateAccessToken(refreshed.accessToken, refreshed.accessTokenExpiresAt);
      res = await doFetch(path, options);
    } else {
      setSession(null);
    }
  }

  if (!res.ok) {
    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson
      ? ((await res.json()) as ApiErrorPayload)
      : ({ error: { code: 'network_error', message: res.statusText } } satisfies ApiErrorPayload);
    throw new ApiError(res.status, payload);
  }
  return res;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const res = await apiFetch(path, options);
  const isJson = res.headers.get('content-type')?.includes('application/json');
  if (!isJson) return undefined as T;
  return (await res.json()) as T;
}
